/** Reads the area and pincode out of a Google reverse-geocode response. See ./CLAUDE.md. */

interface AddressComponent {
  long_name?: string;
  types?: Array<string>;
}

interface GeoCodeResult {
  types?: Array<string>;
  address_components?: Array<AddressComponent>;
  geometry?: { location_type?: string };
}

interface GeoCodeResponse {
  results?: Array<GeoCodeResult>;
}

const AREA_TYPES: Array<string> = ['sublocality_level_2', 'sublocality_level_1', 'neighborhood'];
const POSTAL_CODE = 'postal_code';
const ROOFTOP = 'ROOFTOP';

function readResults(response: unknown): Array<GeoCodeResult> {
  const results = (response as GeoCodeResponse)?.results;
  return Array.isArray(results) ? results : [];
}

function componentValue(result: GeoCodeResult, type: string): string {
  const component = result.address_components?.find((each: AddressComponent): boolean => !!each?.types?.includes(type));
  return component?.long_name ?? '';
}

function readAreaComponents(result: GeoCodeResult): string {
  const parts = ['sublocality_level_2', 'sublocality_level_1']
    .map((type: string): string => componentValue(result, type))
    .filter((value: string): boolean => !!value);
  if (parts.length) return Array.from(new Set(parts)).join(', ');
  return componentValue(result, 'neighborhood');
}

function selectArea(results: Array<GeoCodeResult>): string {
  const areaResult = results.find((each: GeoCodeResult): boolean => !!each?.types
    ?.some((type: string): boolean => AREA_TYPES.includes(type)));
  const fromAreaResult = areaResult ? readAreaComponents(areaResult) : '';
  if (fromAreaResult) return fromAreaResult;

  const roofTopArea = results
    .filter((each: GeoCodeResult): boolean => each?.geometry?.location_type === ROOFTOP)
    .map(readAreaComponents)
    .find((area: string): boolean => !!area);
  return roofTopArea ?? '';
}

function selectPincode(results: Array<GeoCodeResult>): number | null {
  let pincode = 0;
  results.forEach((result: GeoCodeResult): void => {
    if (!result?.types?.includes(POSTAL_CODE)) return;
    result.address_components?.forEach((component: AddressComponent): void => {
      if (!pincode) pincode = Number(component?.long_name);
    });
  });
  return pincode || null;
}

export function extractArea(response: unknown): string {
  return selectArea(readResults(response));
}

export function extractPincode(response: unknown): number | null {
  return selectPincode(readResults(response));
}
